import { CalendarDays, Clock, MapPin, type LucideIcon } from 'lucide-react';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatTime } from '@/utils/date';
import type { MoTravelDay } from '../types/moTravel.types';
import { formatFullDay } from '../utils/dayLabels';
import { getStartTitle } from '../utils/dayRoutes';
import { MoEntryCard, MoEntryMapsLink } from './MoEntryCard';
import { MoStartIcon } from './MoStartIcon';

interface StartFactRowProps {
  icon: LucideIcon;
  label: string;
  value: string;
}

function StartFactRow({ icon: Icon, label, value }: StartFactRowProps) {
  return (
    <li className="flex items-center justify-between gap-3 px-2.5 py-2 text-xs">
      <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground">
        <Icon className="size-3.5" aria-hidden />
        {label}
      </span>
      <span className="min-w-0 truncate text-right text-xs-plus font-semibold tabular-nums">
        {value}
      </span>
    </li>
  );
}

interface MoStartCardProps {
  day: MoTravelDay;
  headerColor: string;
  onClose?: (() => void) | undefined;
  hasTintedBody?: boolean | undefined;
  className?: string | undefined;
}

export function MoStartCard({
  day,
  headerColor,
  onClose,
  hasTintedBody,
  className,
}: MoStartCardProps) {
  if (!day.start) return null;

  return (
    <MoEntryCard
      headerColor={headerColor}
      headerMarker={<MoStartIcon className="size-3" />}
      heading="Start"
      date={day.date}
      closeLabel="Close start details"
      onClose={onClose}
      hasTintedBody={hasTintedBody}
      className={className}
    >
      <ul className="flex flex-col divide-y rounded-md bg-muted/60 max-lg:border">
        <StartFactRow icon={MapPin} label="Started from" value={getStartTitle(day)} />
        <StartFactRow
          icon={Clock}
          label="Logged in"
          value={day.startTime ? formatTime(day.startTime) : EMPTY_VALUE}
        />
        <StartFactRow icon={CalendarDays} label="Date" value={formatFullDay(day.date)} />
      </ul>
      <div className="flex items-center border-t pt-2">
        <MoEntryMapsLink position={[day.start.lat, day.start.lon]} />
      </div>
    </MoEntryCard>
  );
}
